import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from './ui/sheet';
import { Alert, AlertDescription } from './ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { X, Info } from 'lucide-react';

interface Book {
  book_id: string;
  title: string;
  authors: string[];
  cover_url?: string;
}

interface PairwiseSheetProps {
  isOpen: boolean;
  onClose: () => void;
  currentBook: {
    id: string;
    title: string;
    authors: string[];
    cover_url?: string;
  };
  tier: number;
  candidates: Book[];
  onComplete?: () => void;
  onSkip?: () => void;
  placementState?: any;
  comparison?: any;
}

export function PairwiseSheet({
  isOpen,
  onClose,
  currentBook,
  tier,
  candidates,
  onComplete,
  onSkip,
  placementState,
  comparison
}: PairwiseSheetProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completedComparisons, setCompletedComparisons] = useState(0);
  const [currentState, setCurrentState] = useState(placementState);
  const [currentComparison, setCurrentComparison] = useState(comparison);
  const [history, setHistory] = useState<Array<{ state: any; comparison: any; result: string }>>([]);

  useEffect(() => {
    if (isOpen) {
      setCompletedComparisons(0);
      setCurrentState(placementState);
      setCurrentComparison(comparison);
      setHistory([]);
    }
  }, [isOpen, placementState, comparison]);

  const submitDecision = async (comparisonResult: 'better' | 'worse') => {
    setIsSubmitting(true);
    
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        toast.error('Please sign in to continue');
        return;
      }

      const { data, error } = await supabase.functions.invoke('placement-algorithm', {
        body: {
          userId: userData.user.id,
          bookId: currentBook.id,
          tier,
          comparisonResult,
          state: currentState
        }
      });

      if (error) throw error;

      // Save to history for undo
      setHistory(prev => [...prev, { 
        state: currentState, 
        comparison: currentComparison, 
        result: comparisonResult 
      }]);

      setCompletedComparisons(prev => prev + 1);

      if (data.phase === 'complete') {
        toast.success(`Book placed at position ${data.finalPosition}!`);
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('rankings-updated'));
        onComplete?.();
        onClose();
      } else {
        // Update state and comparison for next round
        setCurrentState(data.state);
        setCurrentComparison(data.comparison);
      }
    } catch (error) {
      console.error('Error submitting comparison:', error);
      toast.error('Failed to save comparison');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    toast.info('You can complete this ranking later from your Rank Later queue');
    onSkip?.();
    onClose();
  };

  const handleChoice = (choice: 'better' | 'worse') => {
    submitDecision(choice);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    
    const lastEntry = history[history.length - 1];
    setCurrentState(lastEntry.state);
    setCurrentComparison(lastEntry.comparison);
    setHistory(prev => prev.slice(0, -1));
    setCompletedComparisons(prev => Math.max(0, prev - 1));
    toast.info('Undone last comparison');
  };

  if (!currentComparison?.candidateBook) {
    return null;
  }

  const candidateBook = currentComparison.candidateBook;
  const progress = currentComparison.progress;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[80vh]">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Which book is better?</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
          {progress && (
            <div className="text-sm text-muted-foreground space-y-1">
              <div>Finding position: {progress.low + 1} - {progress.high + 1} of {progress.total}</div>
              <div>Questions: {progress.questionsAsked || 0} / {progress.maxQuestions || 0}</div>
              {currentState?.phase && (
                <div className="text-xs italic">
                  {currentState.phase === 'anchor' ? 'Finding anchor points...' : 'Narrowing down position...'}
                </div>
              )}
            </div>
          )}
          <Alert className="mt-3">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              We use <strong>smart ranking</strong> to find the perfect position. You won't compare every book—we use 
              your previous choices to infer relationships. If you said A &gt; B and B &gt; C before, we know A &gt; C!
            </AlertDescription>
          </Alert>
        </SheetHeader>

        <div className="flex flex-col gap-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Book */}
            <Card className="border-2 border-primary/20">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  {currentBook.cover_url && (
                    <img
                      src={currentBook.cover_url}
                      alt={currentBook.title}
                      className="w-16 h-24 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold line-clamp-2">{currentBook.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {currentBook.authors.join(', ')}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-lg font-bold text-primary">
                        {tier}★
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* VS */}
            <div className="flex items-center justify-center md:hidden">
              <span className="text-xl font-bold text-muted-foreground">VS</span>
            </div>

            {/* Candidate Book */}
            <Card className="border-2 border-secondary/20">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  {candidateBook.cover_url && (
                    <img
                      src={candidateBook.cover_url}
                      alt={candidateBook.title}
                      className="w-16 h-24 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold line-clamp-2">{candidateBook.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {candidateBook.authors.join(', ')}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-lg font-bold text-secondary">
                        {tier}★
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Hidden VS for desktop */}
          <div className="hidden md:flex items-center justify-center -my-3">
            <span className="text-2xl font-bold text-muted-foreground bg-background px-4">VS</span>
          </div>

          {/* Choice buttons */}
          <div className="flex flex-col gap-3">
            <div className="flex gap-3">
              <Button
                onClick={() => handleChoice('better')}
                disabled={isSubmitting}
                variant="default"
                size="lg"
                className="flex-1"
              >
                Left is Better
              </Button>
              <Button
                onClick={() => handleChoice('worse')}
                disabled={isSubmitting}
                variant="secondary"
                size="lg"
                className="flex-1"
              >
                Right is Better
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex gap-2">
              <p>Comparisons: {completedComparisons}</p>
              {history.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleUndo}
                  disabled={isSubmitting}
                >
                  Undo Last
                </Button>
              )}
            </div>
            {onSkip && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                disabled={isSubmitting}
              >
                Skip for Now
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
